import Close from '@mui/icons-material/Close';
import {
  alpha,
  inputBaseClasses,
  outlinedInputClasses,
  selectClasses,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment, { inputAdornmentClasses } from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { RateOwnerType, RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { get } from 'lodash-es';
import { useState } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { getRateTypeOptions } from '~pages/Settings/RatesManagement/rateUtils';

import Label from './Label';

type RateAndTypeInputValue = {
  rate: number;
  type: RateType;
};

type Props<T extends FieldValues = RateAndTypeInputValue> = UseControllerProps<T> & {
  isDisabled?: boolean;
  disabledReason?: string;
  // Renders the rate as money — a "$" prefix, and two decimal places
  // whenever the field isn't being typed in.
  isCurrency?: boolean;
  // Overrides the TextField's DOM `id`, which otherwise defaults to `name`.
  // Needed when the same field is rendered twice at once (e.g. a read-only
  // mirror of another rate) — two elements sharing one `name` would
  // otherwise also share one `id`, which is invalid HTML and breaks any
  // selector (including e2e locators) that targets a rate box by its id.
  id?: string;
  isRequired?: boolean;
  label?: React.ReactNode;
  rateOwnerType?: RateOwnerType;
  sx?: SxProps<Theme>;
};

// Fits the longest rate-type label ("Flat Commission") at the Select's 10px font
// once its 14px left and 30px right padding are subtracted.
const TYPE_SELECT_WIDTH = '150px';

const DECIMAL_ENTRY = /^\d*\.?\d*$/;

export default function RateAndTypeInput<
  T extends FieldValues = { [key: string]: RateAndTypeInputValue | null },
>({
  control,
  isDisabled,
  disabledReason,
  id,
  isCurrency,
  isRequired,
  label,
  name,
  rateOwnerType,
  sx,
}: Props<T>) {
  const rateTypeOptions = getRateTypeOptions(rateOwnerType);
  const { field, fieldState } = useController({ control, name });
  const [isEditingRate, setIsEditingRate] = useState(false);

  const rateError =
    get(fieldState.error, 'rate.message') ?? get(fieldState.error, 'root.rate.message');
  const typeError =
    get(fieldState.error, 'type.message') ?? get(fieldState.error, 'root.type.message');

  const hasSelectedType = Boolean(field.value?.type);

  const rateValue = field.value?.rate ?? '';
  const showsFormattedRate =
    Boolean(isCurrency) &&
    !isEditingRate &&
    rateValue !== '' &&
    !Number.isNaN(Number(rateValue));
  // Blur rounds the STORED value to the two decimals the field displays, so
  // what the user sees is what gets submitted. Only a value that actually
  // changes numerically is written back — "15" displays as "15.00" without
  // being rewritten, so focusing a field and leaving it never dirties the form.
  const normalizeCurrencyOnBlur = () => {
    if (!isCurrency || rateValue === '' || Number.isNaN(Number(rateValue))) return;
    const rounded = Number(Number(rateValue).toFixed(2));
    if (rounded === Number(rateValue)) return;
    field.onChange({ ...field.value, rate: String(rounded) });
  };

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(rateError || typeError)}
      fullWidth
      title={isDisabled ? disabledReason : undefined}
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <TextField
        className={isRequired ? 'required' : ''}
        disabled={isDisabled}
        error={Boolean(rateError || typeError)}
        fullWidth
        id={id ?? name}
        onChange={(event) => {
          const nextRate = event.target.value;
          if (isCurrency && !DECIMAL_ENTRY.test(nextRate)) return;
          field.onChange({ ...field.value, rate: nextRate });
        }}
        onKeyDown={(event) => {
          if (event.key === 'e' || event.key === 'E') {
            event.preventDefault();
          }
        }}
        onWheel={(event) => (event.target as HTMLElement)?.blur()}
        onFocus={() => setIsEditingRate(true)}
        onBlur={() => {
          setIsEditingRate(false);
          normalizeCurrencyOnBlur();
          field.onBlur();
        }}
        type={isCurrency ? 'text' : 'number'}
        value={showsFormattedRate ? Number(rateValue).toFixed(2) : rateValue}
        slotProps={{
          htmlInput: isCurrency ? { inputMode: 'decimal' } : undefined,
          input: {
            ...(isCurrency
              ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
              : {}),
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ position: 'relative', alignItems: 'center' }}
              >
                <Select
                  disabled={isDisabled}
                  error={Boolean(typeError)}
                  onChange={(event: SelectChangeEvent) => {
                    const selectedType = rateTypeOptions.find((type) => {
                      return type.value === event.target.value;
                    });

                    field.onChange({ ...field.value, type: selectedType ?? null });
                  }}
                  value={field.value?.type?.value ?? ''}
                  MenuProps={{
                    // Matches the Contact Type combobox's Autocomplete
                    // listbox (src/components/FormFields/Parts/SelectOption.tsx)
                    // exactly, rather than MUI Select's own defaults: no
                    // border, elevation-4 (not elevation-8) shadow, the app's
                    // yellow highlight instead of a generic gray hover, and
                    // the option label's real 12px/500 size — SelectOption
                    // renders its label in a nested Typography (body2, which
                    // this theme sets to 12px), not the option <li> itself,
                    // which stays at the browser's inherited 16px and is easy
                    // to mismeasure as the visible text size.
                    elevation: 4,
                    sx: (theme) => ({
                      '& .MuiPaper-root': { mt: '3px' },
                      '& .MuiMenuItem-root': {
                        minHeight: '48px',
                        px: theme.spacing(1.5),
                        fontSize: '12px',
                        fontWeight: 500,
                        borderLeft: '3px solid transparent',
                        '&:hover, &.Mui-selected, &.Mui-focusVisible': {
                          backgroundColor: alpha(theme.brandV2.colors.treadYellow, 0.15),
                          borderLeftColor: theme.brandV2.colors.treadYellow,
                        },
                      },
                    }),
                  }}
                  sx={(theme) => ({
                    [`&.${inputBaseClasses.root}`]: {
                      bgcolor: theme.semantic.surfaceSunken,
                      // The rate field's own outer border already draws the
                      // control's one rounded rect around both halves — a
                      // border-radius here traces a second, near-identical
                      // arc directly under it at the shared corner, which
                      // read as a doubled/overlapping border once that outer
                      // border turned yellow on focus.
                      borderRadius: 0,
                      borderLeft: 'solid 1px rgba(0, 0, 0, 0.23)',
                      width: TYPE_SELECT_WIDTH,
                    },
                    [`.${inputBaseClasses.input}`]: {
                      // Shrunk from 12px: at this width, 12px left "Per Yard"
                      // truncating to "Per Ya…" — only 48px of text room
                      // remains after the left/right padding below, and
                      // "Per Yard" needs about 56px at 12px.
                      fontSize: '10px',
                      p: '8px 14px',
                      // Only one icon (the dropdown arrow, or the clear "x" in
                      // its place — see selectClasses.icon below) ever occupies
                      // this end of the field, so the reserved padding only
                      // needs to fit one 24px icon, not both stacked.
                      pr: '30px !important',
                      // The closed value degrades to an ellipsis rather than
                      // forcing typeSelectWidth to fit every rate-type label —
                      // the number field next to it needs the width more.
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    // A selected type replaces the dropdown arrow with the
                    // clear "x" below (in the same slot) instead of showing
                    // both — at this width the two icons stacked side by
                    // side left no room for the value text and the "x"
                    // visibly overlapped it (e.g. "Per Ton").
                    ...(hasSelectedType
                      ? { [`.${selectClasses.icon}`]: { display: 'none' } }
                      : {}),
                    [`.${outlinedInputClasses.notchedOutline}`]: {
                      border: '0 !important',
                    },
                    // The outline is stripped above so this reads as one
                    // attached control with the rate field, but that also
                    // removes the yellow border every other outlined input in
                    // the app gets for free from theme.palette.primary on
                    // focus (e.g. the Contact Type combobox). Restoring it on
                    // the notchedOutline element itself — the exact element
                    // Contact Type's own focus style targets — reproduces
                    // that same border technique instead of a box-shadow
                    // ring; notchedOutline is an absolutely-positioned overlay
                    // in MUI's outlined-input markup, so giving it a real
                    // border here doesn't shift the adjacent rate field.
                    [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                      border: `2px solid ${theme.palette.primary.main} !important`,
                    },
                  })}
                >
                  {rateTypeOptions.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>

                {hasSelectedType ? (
                  <IconButton
                    disabled={isDisabled}
                    onClick={() => field.onChange({ ...field.value, type: null })}
                    size="small"
                    sx={(theme) => ({
                      height: '24px',
                      position: 'absolute',
                      // Sits in the dropdown arrow's own slot (hidden via
                      // selectClasses.icon above) instead of stacking next to
                      // it, so the two icons never compete for space.
                      right: '4px',
                      top: '50%',
                      transform: 'translateY(calc(-50% - 1px))',
                      width: '24px',
                      '&:hover svg': { color: theme.brandV2.colors.treadRed },
                    })}
                  >
                    <Close sx={{ fontSize: '18px' }} />
                  </IconButton>
                ) : (
                  <Typography
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadGray1,
                      fontSize: '10px',
                      left: '14px',
                      pointerEvents: 'none',
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    })}
                  >
                    {t('form_fields.select_type_placeholder')}
                  </Typography>
                )}
              </InputAdornment>
            ),
          },
        }}
        sx={(theme) => ({
          [`.${inputBaseClasses.root}`]: {
            pr: 0,
            [`&.${inputBaseClasses.focused} .${inputAdornmentClasses.root} .${inputBaseClasses.root}`]:
              { borderLeftColor: 'rgba(0, 0, 0, 0.23)' },
          },
          // The rate amount is the field's whole purpose — it must never lose
          // the width fight against the unit-type adornment beside it.
          [`.${inputBaseClasses.input}`]: {
            minWidth: '48px',
          },
          [`&:hover .${inputAdornmentClasses.root} .${inputBaseClasses.root}`]: {
            borderLeftColor: 'rgba(0, 0, 0, 0.87)',
          },
          // MUI's default hover state brightens this outer notchedOutline
          // to an opaque border — purely cosmetic, not a focus signal. If
          // a type was just selected, the Select portion keeps its own
          // yellow Mui-focused border (by design) after the picker closes,
          // and that border sits on the exact same edge as this one; the
          // two together read as a doubled/overlapping border the instant
          // the mouse hovers the rate number next to it. Pinning hover to
          // the resting color (MUI's own un-hovered default) removes the
          // coincidence without touching the real focus indicator.
          [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
            borderColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.23)'
                : 'rgba(0, 0, 0, 0.23)',
          },
        })}
      />

      {(rateError || typeError) && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {[rateError ? `${rateError}.` : '', typeError ? `${typeError}.` : ''].join(' ')}
        </FormHelperText>
      )}
    </FormControl>
  );
}
